import {
    Box,
    Button,
    Grid,
    Hidden,
    IconButton,
    Theme,
    Typography,
    useTheme
} from "@mui/material";
import { CartItemDetails, EditCartItemProps } from "./types";
import SchemeInfo from "./SchemeInfo";
import {
    BlackDownArrowIcon,
    DeleteIcons,
    FundTransferArrow
} from "../../assets/SvgIcons";
import { useEffect, useState } from "react";
import CartForm from "./CartForm/index";
import { InvestmentFundType } from "../../redux-store/types/api-types";

const tabButtonStyle = (type: Array<string>, currentType: string, theme: Theme) => ({
    borderRadius: "7px 7px 0px 0px",
    padding: "5px 15px",
    backgroundColor: type.includes(currentType) ? theme.palette.primary[500] : theme.palette.grey[50],
    color: type.includes(currentType) ? theme.palette.textDark.primary : theme.palette.text.secondary,
    boxShadow: "none",
    ...theme.typography.body_500,
    lineHeight: "20px",
    letterSpacing: "0.02px",
    ":hover": {
        boxShadow: "none",
        backgroundColor: type.includes(currentType) ? theme.palette.primary[800] : theme.palette.grey[100],
    }
})

export default function EditCartItem(props: EditCartItemProps): JSX.Element {
    const [currentTransactionType, setCurrentTransactionType] = useState<string>(props.transactionType);
    const [data, setData] = useState<InvestmentFundType>({} as InvestmentFundType);
    const [cartItemDetails, setCartItemDetails] = useState<any>(props.cartItemDetails);
    const isNewPurchase = `${props?.activeCategory ?? ""}`.toLowerCase() === "new purchase"

    useEffect(() => {
        if (!data?.detailsList) return;
        const navData = data.detailsList?.[1];
        setCartItemDetails((prevState: CartItemDetails[]) => {
            const updatedDetails = [...prevState];
            updatedDetails[1] = { label: navData.title, value: navData.value };
            return updatedDetails;
        });
    }, [data]);

    const handleSetState = (object: any) => {
        setData({ ...data, ...object })
    }
    return (
        <Box
            sx={{
                maxHeight: "100%",
                overflowY: "auto",
                paddingRight: "15px"
            }}
        >
            <Box
                sx={(theme: Theme) => ({
                    paddingBottom: "15px",
                    borderBottom: `0.5px solid ${theme.palette.text.secondary}B2`
                })}
            >
                <Typography
                    variant="h1"
                    sx={(theme: Theme) => ({
                        lineHeight: "32.5px",
                        color: theme.palette.text.primary
                    })}
                >
                    Edit
                </Typography>
            </Box>
            <Box sx={{ marginTop: "35px" }}>
                <Hidden lgUp>
                    <Box>
                        <Grid
                            container
                            flexWrap="nowrap"
                            sx={{ columnGap: "30px" }}
                        >
                            <Grid
                                item
                                container
                                xs={10}
                                flexWrap="nowrap"
                                sx={{ columnGap: "15px" }}
                            >
                                <Grid
                                    item
                                    container
                                    flexDirection="column"
                                    sx={{ rowGap: "10px" }}
                                >
                                    <SchemeInfo
                                        schemeImg={props.fromSchemeImg}
                                        schemeName={props.fromSchemeName}
                                        schemeCategory={props.fromSchemeCategory}
                                        schemeSubCategory={props.fromSchemeSubCategory}
                                        schemeRisk={props.fromSchemeRisk}
                                        schemeRiskColor={props.fromSchemeRiskColor}
                                        amcId={props?.amcId}
                                    />
                                    {props.toSchemeName && (
                                        <>
                                            <BlackDownArrowIcon />
                                            <SchemeInfo
                                                schemeImg={props.toSchemeImg}
                                                schemeName={props.toSchemeName}
                                                schemeCategory={props.toSchemeCategory}
                                                schemeSubCategory={props.toSchemeSubCategory}
                                                schemeRisk={props.toSchemeRisk}
                                                schemeRiskColor={props.toSchemeRiskColor}
                                                amcId={props?.amcId}
                                            />
                                        </>
                                    )}
                                </Grid>
                            </Grid>
                            <Grid
                                item
                                container
                                xs={2}
                                justifyContent="flex-end"
                                alignItems="flex-start"
                                sx={{ columnGap: "8px" }}
                            >
                                <IconButton
                                    sx={{
                                        columnGap: "5px",
                                        ":hover": {
                                            borderRadius: "10px"
                                        }
                                    }}
                                    onClick={() => props.onRemove([props.id])}
                                >
                                    <DeleteIcons
                                        width={18}
                                        height={18}
                                    />
                                    <Typography
                                        variant="subtitle_500"
                                        sx={(theme: Theme) => ({
                                            lineHeight: "17.5px",
                                            letterSpacing: "0.02px",
                                            color: theme.palette.error[500]
                                        })}
                                    >
                                        Remove
                                    </Typography>
                                </IconButton>
                            </Grid>
                        </Grid>
                        <Box sx={{ marginTop: "35px" }}>
                            {cartItemDetails?.map((obj: CartItemDetails) => (
                                <Grid
                                    key={obj.label}
                                    container
                                    sx={{ marginBottom: "25px" }}
                                >
                                    <Grid item xs={5}>
                                        <Typography
                                            variant="body_500"
                                            sx={(theme: Theme) => ({
                                                lineHeight: "20px",
                                                letterSpacing: "0.02px",
                                                color: theme.palette.text.secondary
                                            })}
                                        >
                                            {obj.label}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={7}>
                                        <Typography
                                            variant="body_600"
                                            sx={(theme: Theme) => ({
                                                fontSize: "16px",
                                                color: theme.palette.text.primary,
                                                lineHeight: "20px",
                                                letterSpacing: "0.02px",
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                textAlign: 'right'
                                            })}
                                        >
                                            {obj.value}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            ))}
                        </Box>
                        {(["fresh", "additional", "sip"].includes(currentTransactionType)) && (
                            <Box
                                sx={(theme: Theme) => ({
                                    marginTop: "35px",
                                    borderBottom: `0.5px solid ${theme.palette.text.secondary}B2`,
                                    display: "flex",
                                    columnGap: "10px"
                                })}
                            >
                                <Button
                                    variant="contained"
                                    sx={(theme: Theme) => tabButtonStyle(["fresh", "additional"], currentTransactionType, theme)}
                                    onClick={() => setCurrentTransactionType(isNewPurchase ? "fresh" : "additional")}
                                >
                                    Lumpsum
                                </Button>
                                <Button
                                    variant="contained"
                                    sx={(theme: Theme) => tabButtonStyle(["sip"], currentTransactionType, theme)}
                                    onClick={() => setCurrentTransactionType("sip")}
                                >
                                    SIP
                                </Button>
                            </Box>
                        )}
                        <Box sx={{ marginTop: "35px" }}>
                            <CartForm currentTransactionType={currentTransactionType} data={props} handleSetState={handleSetState} />
                        </Box>
                    </Box >
                </Hidden>
                <Hidden lgDown>
                    <Box>
                        <Grid
                            container
                            flexWrap="nowrap"
                            sx={{ columnGap: "15px" }}
                        >
                            <Grid
                                item
                                container
                                xs={10.5}
                                flexWrap="nowrap"
                                sx={{ columnGap: "15px" }}
                            >
                                <Grid
                                    item
                                    xs={props.toSchemeName ? 5 : 11}
                                >
                                    <SchemeInfo
                                        schemeImg={props.fromSchemeImg}
                                        schemeName={props.fromSchemeName}
                                        schemeCategory={props.fromSchemeCategory}
                                        schemeSubCategory={props.fromSchemeSubCategory}
                                        schemeRisk={props.fromSchemeRisk}
                                        schemeRiskColor={props.fromSchemeRiskColor}
                                        amcId={props?.amcId}
                                    />
                                </Grid>
                                {props.toSchemeName && (
                                    <>
                                        <Grid
                                            item
                                            container
                                            xs={2}
                                            justifyContent="center"
                                            alignItems="center"
                                        >
                                            <FundTransferArrow />
                                        </Grid>
                                        <Grid item xs={5}>
                                            <SchemeInfo
                                                schemeImg={props.toSchemeImg}
                                                schemeName={props.toSchemeName}
                                                schemeCategory={props.toSchemeCategory}
                                                schemeSubCategory={props.toSchemeSubCategory}
                                                schemeRisk={props.toSchemeRisk}
                                                schemeRiskColor={props.toSchemeRiskColor}
                                                amcId={props?.amcId}
                                            />
                                        </Grid>
                                    </>
                                )}
                            </Grid>
                            <Grid
                                item
                                container
                                xs={1.5}
                                justifyContent="flex-end"
                                alignItems="flex-start"
                                sx={{ columnGap: "8px" }}
                            >
                                <IconButton
                                    sx={{
                                        columnGap: "5px",
                                        ":hover": {
                                            borderRadius: "10px"
                                        }
                                    }}
                                    onClick={() => props.onRemove([props.id])}
                                >
                                    <DeleteIcons />
                                    <Typography
                                        variant="body_500"
                                        sx={(theme: Theme) => ({
                                            lineHeight: "20px",
                                            letterSpacing: "0.02px",
                                            color: theme.palette.error[500]
                                        })}
                                    >
                                        Remove
                                    </Typography>
                                </IconButton>
                            </Grid>
                        </Grid>
                        <Grid
                            container
                            sx={{
                                marginTop: "35px",
                                flexWrap: "nowrap"
                            }}
                        >
                            {cartItemDetails?.map((obj: CartItemDetails, index: number) => (
                                <Grid
                                    item
                                    container
                                    xs={3}
                                    key={obj.label}
                                    sx={{
                                        rowGap: "8px",
                                        marginRight: index !== cartItemDetails.length - 1 ? "10%" : "0%"
                                    }}
                                >
                                    <Grid item xs={12}>
                                        <Typography
                                            variant="body_500"
                                            sx={(theme: Theme) => ({
                                                lineHeight: "20px",
                                                letterSpacing: "0.02px",
                                                color: theme.palette.text.secondary
                                            })}
                                        >
                                            {obj.label}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Typography
                                            variant="body_600"
                                            sx={(theme: Theme) => ({
                                                fontSize: "16px",
                                                color: theme.palette.text.primary,
                                                lineHeight: "20px",
                                                letterSpacing: "0.02px",
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                            })}
                                        >
                                            {obj.value}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            ))}
                        </Grid>
                        {(["fresh", "additional", "sip"].includes(currentTransactionType)) && (
                            <Box
                                sx={(theme: Theme) => ({
                                    marginTop: "35px",
                                    borderBottom: `0.5px solid ${theme.palette.text.secondary}B2`,
                                    display: "flex",
                                    columnGap: "10px"
                                })}
                            >
                                <Button
                                    variant="contained"
                                    sx={(theme: Theme) => tabButtonStyle(["fresh", "additional"], currentTransactionType, theme)}
                                    onClick={() => setCurrentTransactionType(isNewPurchase ? "fresh" : "additional")}
                                >
                                    Lumpsum
                                </Button>
                                <Button
                                    variant="contained"
                                    sx={(theme: Theme) => tabButtonStyle(["sip"], currentTransactionType, theme)}
                                    onClick={() => setCurrentTransactionType("sip")}
                                >
                                    SIP
                                </Button>
                            </Box>
                        )}
                        <Box sx={{ marginTop: "35px" }}>
                            <CartForm currentTransactionType={currentTransactionType} data={props} handleSetState={handleSetState} />
                        </Box>
                    </Box>
                </Hidden>
            </Box>
        </Box>
    )
}